import React, { useState, useEffect, useRef, useCallback, FunctionComponent } from 'react';
import { Presentation } from './presentation';
import { getCsrfToken } from '../../../libs/csrfToken';
import { sortCompanies } from '../../../services/companies';
import type { Props } from './types';

export const Component: FunctionComponent<Props> = (props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isDropdownOpenRef = useRef(isDropdownOpen);
  isDropdownOpenRef.current = isDropdownOpen;

  const handleSortChange = useCallback(
    async (sortOption: Props['sortBy']) => {
      props.handleModalOn();

      const csrfToken = getCsrfToken();
      const newSortOption = props.sortBy === sortOption ? 'default' : sortOption;

      if (newSortOption === 'default') {
        props.onSortChange(newSortOption, props.initialCompanyIds);
        setIsDropdownOpen(false);
        return;
      }

      try {
        const data = await sortCompanies({
          csrfToken,
          body: JSON.stringify({
            sort_type: newSortOption,
            company_ids: props.companyIds,
          }),
        });

        const validatedCompanyIds = Array.isArray(data?.company_ids)
          ? data.company_ids.map(Number).filter((id) => !isNaN(id))
          : [];

        props.onSortChange(newSortOption, validatedCompanyIds);
      } catch (error) {
        console.error('Error sorting companies:', error);
        props.onSortChange(props.sortBy, props.companyIds);
      }
      setIsDropdownOpen(false);
    },
    [props]
  );

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prevState) => !prevState);
  }, []);

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && isDropdownOpenRef.current) {
      setIsDropdownOpen(false);
    }
  }, []);

  const getSortButtonText = useCallback(() => {
    switch (props.sortBy) {
      case 'recruitment_desc':
        return '求人件数順';
      case 'event_desc':
        return 'イベント件数順';
      case 'story_desc':
        return 'ストーリー件数順';
      default:
        return '並び替え';
    }
  }, [props.sortBy]);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const presentationProps = {
    ...props,
    dropdownRef,
    isDropdownOpen,
    toggleDropdown,
    getSortButtonText,
    handleSortChange,
  };

  return <Presentation {...presentationProps} />;
};
